import React, { LegacyRef, forwardRef, useEffect, useRef } from "react";
import ReactTooltip from "react-tooltip";
import classNames from "classnames";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import IEditableProps from "../../utilities/IEditableProps";
import IStyledProps from "../../utilities/IStyledProps";
import useMediaQuery from '../../utilities/useMediaQuery';
import CloseIcon from '../../icons/close-icon-gray.svg';
import Picture from "../Picture";
import { AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from "../../utilities/automation";

export interface ITooltipProps extends IEditableProps, IStyledProps {
    className?: string;
    html;
    place?: string;
    children?: any;
}

export const TooltipTrigger = forwardRef(({ editorClassObj, className = "", cmsId, html = '<p>hello</p>', place = 'right', children = null }: ITooltipProps, ref: any) => {
    useEffect(() => {
        ReactTooltip.rebuild(); // rebinds for tooltips loaded dynamically
    }, [html]);

    return (
        <span
            data-id={cmsId}
            ref={ref}
            className={classNames("macu-tooltip-trigger", editorClassObj, className)}
            data-for="macu-tooltip"
            data-tip={html}
            data-place={place}>
            {children || <FontAwesomeIcon icon={faInfoCircle} />}
        </span>
    );
});

export function TooltipContent() {
    const isMobile = useMediaQuery('(max-width: 794px)');
    const tooltipRef: LegacyRef<ReactTooltip> = useRef();
    const contentRef = useRef(null);

    // Methods to toggle class that disables body scroll on mobile
    const addBodyClass = () => document.body.classList.add('tooltip-open');
    const removeBodyClass = () => document.body.classList.remove('tooltip-open');

    const closeTooltip = () => {
        if ('tooltipRef' in tooltipRef.current) {
            tooltipRef.current.tooltipRef = null;
        }

        ReactTooltip.hide();
    }

    useEffect(() => {
        ReactTooltip.rebuild(); // rebinds for tooltips loaded dynamically
    });

    useEffect(() => {
        // Function to detect clicks outside content and emulate modal behavior
        function handleClick(e) {
            if (contentRef.current && !contentRef.current.contains(e.target)) {
                closeTooltip();
            }
        }   
        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
        }

    }, [contentRef])

    return (
        <ReactTooltip
            clickable
            uuid="macu-tooltip-uuid"
            id="macu-tooltip"
            className="macu-tooltip-content bg-sand text-dark"
            scrollHide={!isMobile}
            event="click focus"
            globalEventOff="click"
            ref={tooltipRef}
            afterShow={addBodyClass}
            afterHide={removeBodyClass}
            getContent={(dataTip) => {
                const tip = dataTip ? dataTip.replace(/\[\[/g, '<').replace(/\]\]/g, '>') : "";
                return (
                    <div className="macu-tooltip-content__container" ref={contentRef}>
                        <div className="macu-tooltip-content__close-container">
                            <Button
                                variant="link"
                                data-test-id={createDataTestId({feature: AutomationTestIdFeature.TOOLTIP, element: AutomationTestIdElementType.BUTTON, specifier: 'close' })}
                                className="macu-tooltip-content__close-button text-dark px-0"
                                onClick={closeTooltip}
                            >
                                <Picture className="macu-tooltip-content__close-icon" src={CloseIcon} />
                            </Button>
                        </div>
                        <div data-test-id={createDataTestId({feature: AutomationTestIdFeature.TOOLTIP, element: AutomationTestIdElementType.CONTAINER, specifier: 'content' })} className="macu-tooltip-content__content" dangerouslySetInnerHTML={{ __html: tip }}/> 
                    </div>                
                )
            }}
        />
    );
}